import { useState, useEffect, useCallback } from "react";
// import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  axiosCreateCompany,
  axiosGetTemplates,
} from "../../helpers/superAdminAxios";

const ClientOnboarding = () => {
  const { token, notify } = useOutletContext();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    company: {
      name: "",
      address_street: "",
      address_number: "",
      address_postalcode: "",
      phone_number: "",
      country: "",
      website: "",
    },
    admin: {
      email: "",
      first_name: "",
      last_name: "",
      password: "",
    },
    template: null,
  });

  // const { t } = useTranslation();

  const steps = [
    { title: "Company Information", number: 1 },
    { title: "Admin User", number: 2 },
    { title: "Template Selection", number: 3 },
    { title: "Review & Create", number: 4 },
  ];

  const countries = [
    "United States",
    "Canada",
    "United Kingdom",
    "Germany",
    "France",
    "Australia",
    "Netherlands",
    "Sweden",
    "Norway",
    "Denmark",
    "Other",
  ];

  const loadTemplates = useCallback(async () => {
    try {
      const response = await axiosGetTemplates({
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(response.data);
    } catch (error) {
      console.error("Error loading templates:", error);
      notify("Failed to load templates", "error");
    }
  }, [token, notify]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const validateStep = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return formData.company.name.trim() !== "";
      case 2:
        return (
          formData.admin.email.trim() !== "" &&
          formData.admin.first_name.trim() !== "" &&
          formData.admin.last_name.trim() !== "" &&
          formData.admin.password.length >= 6
        );
      case 3:
        return true; // Template selection is optional
      case 4:
        return true; // Review step
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await axiosCreateCompany({
        headers: { Authorization: `Bearer ${token}` },
        data: {
          companyData: formData.company,
          adminUserData: formData.admin,
          templateId: formData.template,
        },
      });

      if (response.data.success) {
        notify("Company created successfully!", "success");
        // Reset form
        setFormData({
          company: {
            name: "",
            address_street: "",
            address_number: "",
            address_postalcode: "",
            phone_number: "",
            country: "",
            website: "",
          },
          admin: {
            email: "",
            first_name: "",
            last_name: "",
            password: "",
          },
          template: null,
        });
        setStep(1);
      }
    } catch (error) {
      console.error("Error creating company:", error);
      notify(
        error.response?.data?.message || "Failed to create company",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Company Information
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company.name}
                  onChange={(e) =>
                    handleInputChange("company", "name", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.company.website}
                  onChange={(e) =>
                    handleInputChange("company", "website", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.company.address_street}
                  onChange={(e) =>
                    handleInputChange(
                      "company",
                      "address_street",
                      e.target.value
                    )
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="123 Main Street"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Street Number
                </label>
                <input
                  type="text"
                  value={formData.company.address_number}
                  onChange={(e) =>
                    handleInputChange(
                      "company",
                      "address_number",
                      e.target.value
                    )
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="123"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={formData.company.address_postalcode}
                  onChange={(e) =>
                    handleInputChange(
                      "company",
                      "address_postalcode",
                      e.target.value
                    )
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="12345"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <select
                  value={formData.company.country}
                  onChange={(e) =>
                    handleInputChange("company", "country", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.company.phone_number}
                  onChange={(e) =>
                    handleInputChange("company", "phone_number", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Admin User Information
            </h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.admin.first_name}
                  onChange={(e) =>
                    handleInputChange("admin", "first_name", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.admin.last_name}
                  onChange={(e) =>
                    handleInputChange("admin", "last_name", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Doe"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.admin.email}
                  onChange={(e) =>
                    handleInputChange("admin", "email", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="john.doe@company.com"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.admin.password}
                  onChange={(e) =>
                    handleInputChange("admin", "password", e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Minimum 6 characters"
                />
                <p className="mt-1 text-sm text-gray-500">
                  This will be the admin's login password
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Template Selection
            </h3>
            <p className="text-sm text-gray-600">
              Choose a survey template for this company. If no template is
              selected, the default template will be used.
            </p>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="default-template"
                  name="template"
                  value=""
                  checked={formData.template === null}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, template: null }))
                  }
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                />
                <label
                  htmlFor="default-template"
                  className="ml-3 block text-sm font-medium text-gray-700"
                >
                  Use Default Template (6 Elements of good leadership)
                </label>
              </div>
              {templates.map((template) => (
                <div key={template.id} className="flex items-start">
                  <input
                    type="radio"
                    id={`template-${template.id}`}
                    name="template"
                    value={template.id}
                    checked={formData.template === template.id}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        template: template.id,
                      }))
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 mt-1"
                  />
                  <label
                    htmlFor={`template-${template.id}`}
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    <div className="font-medium">{template.title}</div>
                    <div className="text-gray-500">
                      {template.categories_count} categories,{" "}
                      {template.tags.join(", ")}
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">
              Review & Create
            </h3>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div>
                <h4 className="font-medium text-gray-900">Company Details</h4>
                <div className="mt-2 text-sm text-gray-600">
                  <p>
                    <strong>Name:</strong> {formData.company.name}
                  </p>
                  <p>
                    <strong>Website:</strong>{" "}
                    {formData.company.website || "Not provided"}
                  </p>
                  <p>
                    <strong>Country:</strong>{" "}
                    {formData.company.country || "Not provided"}
                  </p>
                  <p>
                    <strong>Address:</strong>{" "}
                    {formData.company.address_street &&
                    formData.company.address_number
                      ? `${formData.company.address_number} ${formData.company.address_street}`
                      : "Not provided"}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Admin User</h4>
                <div className="mt-2 text-sm text-gray-600">
                  <p>
                    <strong>Name:</strong> {formData.admin.first_name}{" "}
                    {formData.admin.last_name}
                  </p>
                  <p>
                    <strong>Email:</strong> {formData.admin.email}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Template</h4>
                <div className="mt-2 text-sm text-gray-600">
                  <p>
                    {formData.template
                      ? templates.find((t) => t.id === formData.template)?.title
                      : "Default Template (6 Elements of good leadership)"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Client Onboarding</h1>
        <p className="mt-2 text-gray-600">
          Create a new company with admin user and survey template
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((stepItem, stepIdx) => (
              <li
                key={stepItem.title}
                className={`${
                  stepIdx !== steps.length - 1 ? "pr-8 sm:pr-20" : ""
                } relative`}
              >
                <div className="flex items-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                      step <= stepItem.number
                        ? "border-indigo-600 bg-indigo-600"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    <span
                      className={`text-sm font-medium ${
                        step <= stepItem.number ? "text-white" : "text-gray-500"
                      }`}
                    >
                      {stepItem.number}
                    </span>
                  </div>
                  <span
                    className={`ml-4 text-sm font-medium ${
                      step <= stepItem.number
                        ? "text-indigo-600"
                        : "text-gray-500"
                    }`}
                  >
                    {stepItem.title}
                  </span>
                </div>
                {stepIdx !== steps.length - 1 && (
                  <div
                    className={`absolute top-4 left-4 h-0.5 w-full ${
                      step > stepItem.number ? "bg-indigo-600" : "bg-gray-300"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* Step Content */}
      <div className="bg-white shadow rounded-lg p-6">
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={step === 1}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <div className="flex space-x-3">
          {step < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!validateStep(step)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating..." : "Create Company"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientOnboarding;
